import React, { useCallback, useMemo } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import { getRanges } from '../../app/pages/reportAnalytics/ReportFilterOptions';
import moment from 'moment-timezone';
import {
    API_DATE_FORMAT,
    formatDateRangeDisplay,
    getLocaleDateFormat,
    parseFlexibleDate,
} from '../../utils/dateLocale';

export const CLEAR_DATE_FILTER_LABEL = 'Clear Date Filter';

const API_PARSE_FORMATS = [API_DATE_FORMAT, 'MM-DD-YYYY', 'M/D/YYYY', 'M-D-YYYY'] as const;

function resolvePickerDate(
    value: string | moment.Moment | Date | null | undefined,
    fallback: moment.Moment
): moment.Moment {
    if (value == null || value === '') return fallback;
    if (moment.isMoment(value)) return value.isValid() ? value : fallback;
    if (value instanceof Date) {
        const m = moment(value);
        return m.isValid() ? m : fallback;
    }

    const trimmed = String(value).trim();
    // Filter state is stored as API US format — try that before locale (avoids 09/02 → Feb 9).
    for (const fmt of API_PARSE_FORMATS) {
        const m = moment(trimmed, fmt, true);
        if (m.isValid()) return m;
    }

    return parseFlexibleDate(trimmed) || fallback;
}

const DateRangeComp = ({
    startDate,
    endDate,
    onApply,
    onCancel,
    value,
    label = 'Date Range',
    required,
    className,
}: any) => {
    const localeDateFormat = useMemo(() => getLocaleDateFormat(), []);

    const ranges = useMemo(
        () => ({
            ...getRanges(),
            // Sentinel dates — ignored when Clear is chosen (see handleApply)
            [CLEAR_DATE_FILTER_LABEL]: [moment(), moment()],
        }),
        []
    );

    const resolvedStart = useMemo(
        () => resolvePickerDate(startDate, moment().subtract(6, 'days')),
        [startDate]
    );

    const resolvedEnd = useMemo(
        () => resolvePickerDate(endDate, moment()),
        [endDate]
    );

    const displayValue = useMemo(() => {
        const start = resolvePickerDate(startDate, moment.invalid());
        const end = resolvePickerDate(endDate, moment.invalid());
        if (start.isValid() && end.isValid()) {
            return formatDateRangeDisplay(startDate, endDate);
        }
        if (
            value &&
            value !== 'MM/DD/YYYY' &&
            value !== 'MM-DD-YYYY' &&
            value !== localeDateFormat &&
            !String(value).includes('Invalid')
        ) {
            const parts = String(value).split(/\s*-\s*/);
            if (parts.length === 2) {
                const s = resolvePickerDate(parts[0], moment.invalid());
                const e = resolvePickerDate(parts[1], moment.invalid());
                if (s.isValid() && e.isValid()) {
                    return formatDateRangeDisplay(parts[0], parts[1]);
                }
            }
            return value;
        }
        return '';
    }, [startDate, endDate, value, localeDateFormat]);

    const handleApply = useCallback(
        (event: any, picker: any) => {
            if (picker?.chosenLabel === CLEAR_DATE_FILTER_LABEL) {
                onCancel?.(event, picker);
                return;
            }
            onApply?.(event, picker);
        },
        [onApply, onCancel]
    );

    return (
        <div className={`ss-params-field ${className || ''}`.trim()}>
            {label && (
                <div className='d-flex justify-content-between align-items-center'>
                    <label className="form-label fw-bold text-dark fs-6 text-nowrap">
                        {label}{String(label).endsWith(':') ? '' : ':'}
                        {required && <span className="required-star text-danger ms-1">*</span>}
                    </label>
                </div>
            )}
            <DateRangePicker
                key={`${resolvedStart.format('YYYY-MM-DD')}-${resolvedEnd.format('YYYY-MM-DD')}-${localeDateFormat}`}
                initialSettings={{
                    startDate: resolvedStart,
                    endDate: resolvedEnd,
                    ranges,
                    locale: {
                        format: localeDateFormat,
                        cancelLabel: 'Clear',
                    },
                    autoUpdateInput: false,
                }}
                onApply={handleApply}
                onCancel={onCancel}
            >
                <input
                    type="text"
                    className="form-select form-select-solid cursor-pointer default-input ss-date-field"
                    value={displayValue}
                    placeholder={localeDateFormat}
                    readOnly
                />
            </DateRangePicker>
        </div>
    );
};

export default DateRangeComp;
